import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IFebLocalisation } from 'src/app/shared/models/feb-localisation';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { FebLocationsService } from '../feb-locations.service';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
@Component({
  selector: 'app-feb-location-form',
  templateUrl: './feb-location-form.component.html',
  styleUrls: ['./feb-location-form.component.less']
})
export class FebLocationFormComponent {

  formGroup: UntypedFormGroup;
  isEditMode = false;
  errorMessage: string | null = null;

  @Output()
  updateEvent = new EventEmitter<IFebLocalisation>();

  @Output()
  clearEvent = new EventEmitter<any>();

  @Input()
  localisation!: IFebLocalisation;

  readonly matcher = new ShowOnDirtyErrorStateMatcher();

  constructor(
    private _fb: UntypedFormBuilder,
    private febLocationsService: FebLocationsService,
    private snackbarService: SnackbarService,
    private dialogService: DialogService
  ) {
    this.formGroup = this._fb.group({
      localisationLabel: ['', [
        Validators.maxLength(150),
        Validators.required,
        Validators.pattern(/^(?=.*\D)(?:\D*\d){0,3}\D*$/)
      ]],
    });
    this.formGroup.get('localisationLabel')?.valueChanges.subscribe(() => {
      this.errorMessage = null;
    });
  }

  onSubmit(): void {
    if (this.formGroup.invalid) return;

    if (!this.isEditMode) {
      this.febLocationsService.create(this.formGroup.value).subscribe({
        next: data => {
          this.updateEvent.emit(data);
          this.formGroup.reset();
          this.snackbarService.openSnackBar('Localisation créée avec succès');
        },
        error: (errFn) => {
          const err = typeof errFn === 'function' ? errFn() : errFn;
          const msg = err?.error?.message ?? 'Erreur lors de la création de la localisation';
          this.errorMessage = msg;
          this.snackbarService.openSnackBar(msg, 'error-snack-bar');
        }
      });
    } else {
      this.febLocationsService.update(this.localisation.id ?? '', this.formGroup.value).subscribe({
        next: data => {
          this.updateEvent.emit(data);
          this.formGroup.reset();
          this.isEditMode = false;
          this.snackbarService.openSnackBar('Localisation modifiée avec succès');
        },
        error: (errFn) => {
          const err = typeof errFn === 'function' ? errFn() : errFn;
          const msg = err?.error?.message ?? 'Erreur lors de la modification de la localisation';
          this.errorMessage = msg;
          this.snackbarService.openSnackBar(msg, 'error-snack-bar');
        }
      });
    }
  }

  clear(): void {
    this.formGroup.reset();
    this.isEditMode = false;
    this.errorMessage = null;
    this.updateEvent.emit();
  }

  updateForm(): void {
    this.formGroup.setValue({ localisationLabel: this.localisation.localisationLabel });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['localisation'] && changes['localisation'].currentValue) {
      const loc = changes['localisation'].currentValue;
      if (loc.id) {
        this.updateForm();
        this.isEditMode = true;
      } else {
        this.clear();
      }
    }
  }

  confirmDelete(): void {
    this.dialogService.openConfirmDialog(this.localisation.localisationLabel ?? '')
      .afterClosed().subscribe({
        next: res => {
          if (res) {
            this.febLocationsService.delete(this.localisation).subscribe({
              next: () => {
                this.updateEvent.emit();
                this.clear();
                this.snackbarService.openSnackBar('Localisation supprimée avec succès');
              },
              error: (err) => {
                const msg = err?.error?.message ?? err?.error ?? null;
                this.errorMessage = typeof msg === 'string'
                  ? msg
                  : 'Erreur lors de la suppression de la localisation';
                this.snackbarService.openSnackBar(this.errorMessage!, 'error-snack-bar');
              }
            });
          }
        }
      });
  }
}
